import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../../config';
import { useLanguage } from '../../contexts/LanguageContext';
import DecisionHistory from './DecisionHistory';
import './TrainingGoals.css';

interface TrainingGoal {
  id: number;
  title: string;
  description: string | null;
  event_type: string | null;
  target_date: string | null;
  current_ftp: number | null;
  target_ftp: number | null;
  weekly_hours: number | null;
  priority: string;
  is_active: boolean;
  created_at: string;
  number_of_days: number | null;
  total_elevation: number | null;
  total_distance: number | null;
  daily_stages: any[] | null;
  event_data: EventData | null;
  has_weight_goal: boolean;
  current_weight: number | null;
  target_weight: number | null;
  weight_target_date: string | null;
  route_source: string | null;
  route_polyline: string | null;
  route_distance: number | null;
  route_elevation_gain: number | null;
  strava_route_url: string | null;
  gpx_file_url: string | null;
  goal_category: string | null;
  metric_type: string | null;
  current_value: number | null;
  target_value: number | null;
}

interface EventData {
  countries?: string[];
  highlights?: string[];
  stages?: Stage[];
}

interface Stage {
  number: number;
  title: string;
  date?: string;
  start_location?: string;
  end_location?: string;
  distance?: number;
  elevation?: number;
  short_distance?: number;
  short_elevation?: number;
  countries?: string[];
  climbs?: Climb[];
  highlights?: string[];
}

interface Climb {
  name: string;
  peak_elevation?: number;
  distance?: number;
  elevation_gain?: number;
  avg_gradient?: number;
  max_gradient?: number;
}

interface RaceEvent {
  name: string;
  date: string;
  priority: 'A' | 'B' | 'C';
}

function decodePolyline(encoded: string): [number, number][] {
  const points: [number, number][] = [];
  let index = 0, lat = 0, lng = 0;
  while (index < encoded.length) {
    let shift = 0, result = 0, byte: number;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { byte = encoded.charCodeAt(index++) - 63; result |= (byte & 0x1f) << shift; shift += 5; } while (byte >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    points.push([lat / 1e5, lng / 1e5]);
  }
  return points;
}

function PolylinePreview({ polyline, width = 280, height = 120 }: { polyline: string; width?: number; height?: number }) {
  const points = decodePolyline(polyline);
  if (points.length < 2) return null;
  const lats = points.map(p => p[0]);
  const lngs = points.map(p => p[1]);
  const minLat = Math.min(...lats), maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
  const pad = 10;
  const w = width - pad * 2, h = height - pad * 2;
  const rangeX = maxLng - minLng || 0.001, rangeY = maxLat - minLat || 0.001;
  const scale = Math.min(w / rangeX, h / rangeY);
  const svgPoints = points.map(([lat, lng]) => {
    const x = pad + (lng - minLng) * scale;
    const y = pad + (maxLat - lat) * scale;
    return `${x},${y}`;
  }).join(' ');
  const svgW = rangeX * scale + pad * 2;
  const svgH = rangeY * scale + pad * 2;
  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} width={width} height={height} className="tg-route-svg">
      <polyline points={svgPoints} fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={svgPoints.split(' ')[0].split(',')[0]} cy={svgPoints.split(' ')[0].split(',')[1]} r="4" fill="#5cb85c" />
      <circle cx={svgPoints.split(' ').slice(-1)[0].split(',')[0]} cy={svgPoints.split(' ').slice(-1)[0].split(',')[1]} r="4" fill="#d9534f" />
    </svg>
  );
}

const EVENT_TYPES = ['gran_fondo', 'criterium', 'mtb', 'multi_stage_trip', 'race', 'training_camp', 'other'];

const EVENT_TYPE_ICONS: Record<string, string> = {
  gran_fondo: '\u{1F6B4}',
  criterium: '\u{1F3C1}',
  mtb: '\u{26F0}\uFE0F',
  multi_stage_trip: '\u{1F5FA}\uFE0F',
  race: '\u{1F3C6}',
  training_camp: '\u{1F3D5}\uFE0F',
  other: '\u{1F3AF}',
};

interface FormData {
  title: string;
  description: string;
  event_type: string;
  target_date: string;
  priority: string;
  number_of_days: string;
  total_distance: string;
  total_elevation: string;
}

const emptyForm: FormData = {
  title: '',
  description: '',
  event_type: '',
  target_date: '',
  priority: 'primary',
  number_of_days: '',
  total_distance: '',
  total_elevation: '',
};

interface FitnessFormData {
  metric_type: string;
  current_value: string;
  target_value: string;
  target_date: string;
}

const emptyFitnessForm: FitnessFormData = {
  metric_type: 'weight',
  current_value: '',
  target_value: '',
  target_date: '',
};

interface TrainingGoalsProps {
  editGoalId?: number | null;
  onEditGoalHandled?: () => void;
  onGeneratePlan?: (goalTitle: string) => void;
}

const TrainingGoals: React.FC<TrainingGoalsProps> = ({ editGoalId, onEditGoalHandled, onGeneratePlan }) => {
  const { t } = useLanguage();
  const [goals, setGoals] = useState<TrainingGoal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<TrainingGoal | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [formMode, setFormMode] = useState<'manual' | 'upload'>('manual');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [expandedGoals, setExpandedGoals] = useState<Set<number>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [extractedEventData, setExtractedEventData] = useState<EventData | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gpxInputRef = useRef<HTMLInputElement>(null);
  const [routeMode, setRouteMode] = useState<'none' | 'strava' | 'gpx' | 'activity'>('none');
  const [stravaUrl, setStravaUrl] = useState('');
  const [activityId, setActivityId] = useState('');
  const [routeUploading, setRouteUploading] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showFitnessModal, setShowFitnessModal] = useState(false);
  const [fitnessFormData, setFitnessFormData] = useState<FitnessFormData>(emptyFitnessForm);
  const [editingFitnessGoal, setEditingFitnessGoal] = useState<TrainingGoal | null>(null);
  const [fitnessSaving, setFitnessSaving] = useState(false);
  const [fitnessSaveError, setFitnessSaveError] = useState<string | null>(null);

  // Race Calendar state
  const [events, setEvents] = useState<RaceEvent[]>([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEventIdx, setEditingEventIdx] = useState<number | null>(null);
  const [eventForm, setEventForm] = useState({ name: '', date: '', priority: 'B' as string });

  const fetchGoals = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/training/goals`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch goals');
      const data = await res.json();
      setGoals(Array.isArray(data) ? data : data.goals || []);
      setError(null);
    } catch (err) {
      setError('Failed to load goals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  // Race Calendar fetch & handlers
  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/coach/training-cycle/events`, { credentials: 'include' });
      if (res.ok) setEvents(await res.json());
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Handle editGoalId from calendar
  useEffect(() => {
    if (editGoalId && goals.length > 0) {
      const goal = goals.find(g => g.id === editGoalId);
      if (goal) {
        setEditingGoal(goal);
        setFormData({
          title: goal.title || '',
          description: goal.description || '',
          event_type: goal.event_type || '',
          target_date: goal.target_date || '',
          priority: goal.priority || 'primary',
          number_of_days: goal.number_of_days?.toString() || '',
          total_distance: goal.total_distance?.toString() || '',
          total_elevation: goal.total_elevation?.toString() || '',
        });
        if (goal.event_data) setExtractedEventData(goal.event_data);
        setFormMode('manual');
        setShowModal(true);
      }
      onEditGoalHandled?.();
    }
  }, [editGoalId, goals, onEditGoalHandled]);

  const saveEvent = async () => {
    if (!eventForm.name.trim() || !eventForm.date) return;
    const method = editingEventIdx !== null ? 'PUT' : 'POST';
    const body = editingEventIdx !== null
      ? { index: editingEventIdx, ...eventForm }
      : eventForm;
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/coach/training-cycle/events`, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setEvents(await res.json());
        setShowEventForm(false);
        setEditingEventIdx(null);
        setEventForm({ name: '', date: '', priority: 'B' });
      }
    } catch {
      // silent
    }
  };

  const deleteEvent = async (idx: number) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/coach/training-cycle/events`, {
        method: 'DELETE', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ index: idx }),
      });
      if (res.ok) setEvents(await res.json());
    } catch {
      // silent
    }
  };

  const openAddModal = () => {
    setEditingGoal(null);
    setFormData(emptyForm);
    setFormMode('manual');
    setUploadSuccess(false);
    setSaveError(null);
    setShowModal(true);
  };

  const openEditModal = (goal: TrainingGoal) => {
    setEditingGoal(goal);
    setFormData({
      title: goal.title || '',
      description: goal.description || '',
      event_type: goal.event_type || '',
      target_date: goal.target_date || '',
      priority: goal.priority || 'primary',
      number_of_days: goal.number_of_days?.toString() || '',
      total_distance: goal.total_distance?.toString() || '',
      total_elevation: goal.total_elevation?.toString() || '',
    });
    setFormMode('manual');
    setUploadSuccess(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingGoal(null);
    setFormData(emptyForm);
    setUploadSuccess(false);
    setSaveError(null);
    setExtractedEventData(null);
  };

  const openFitnessModal = (goal?: TrainingGoal) => {
    if (goal) {
      setEditingFitnessGoal(goal);
      setFitnessFormData({
        metric_type: goal.metric_type || 'weight',
        current_value: goal.current_value?.toString() || '',
        target_value: goal.target_value?.toString() || '',
        target_date: goal.target_date || '',
      });
    } else {
      setEditingFitnessGoal(null);
      setFitnessFormData(emptyFitnessForm);
    }
    setFitnessSaveError(null);
    setShowFitnessModal(true);
  };

  const closeFitnessModal = () => {
    setShowFitnessModal(false);
    setEditingFitnessGoal(null);
    setFitnessFormData(emptyFitnessForm);
    setFitnessSaveError(null);
  };

  const handleFitnessSave = async () => {
    if (!fitnessFormData.metric_type) return;
    setFitnessSaving(true);
    setFitnessSaveError(null);
    try {
      const metricLabel = fitnessFormData.metric_type === 'weight' ? 'Weight' : 'FTP';
      const body: any = {
        title: `${metricLabel} Goal`,
        goal_category: 'fitness',
        metric_type: fitnessFormData.metric_type,
        current_value: fitnessFormData.current_value ? parseFloat(fitnessFormData.current_value) : null,
        target_value: fitnessFormData.target_value ? parseFloat(fitnessFormData.target_value) : null,
        target_date: fitnessFormData.target_date || null,
        priority: 'primary',
      };

      const url = editingFitnessGoal
        ? `${API_BASE_URL}/api/training/goals/${editingFitnessGoal.id}`
        : `${API_BASE_URL}/api/training/goals`;
      const method = editingFitnessGoal ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save fitness goal');
      }
      closeFitnessModal();
      fetchGoals();
    } catch (err: any) {
      setFitnessSaveError(err.message || 'Failed to save fitness goal');
    } finally {
      setFitnessSaving(false);
    }
  };

  const handleFormChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const body: any = {
        title: formData.title,
        description: formData.description || null,
        event_type: formData.event_type || null,
        target_date: formData.target_date || null,
        priority: formData.priority,
        number_of_days: formData.number_of_days ? parseInt(formData.number_of_days) : null,
        total_distance: formData.total_distance ? parseFloat(formData.total_distance) : null,
        total_elevation: formData.total_elevation ? parseFloat(formData.total_elevation) : null,
        goal_category: 'event',
      };
      if (extractedEventData && !editingGoal) {
        body.event_data = extractedEventData;
      }

      const url = editingGoal
        ? `${API_BASE_URL}/api/training/goals/${editingGoal.id}`
        : `${API_BASE_URL}/api/training/goals`;
      const method = editingGoal ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || t('aiCoach.goals.saveFailed') || 'Failed to save goal');
      }
      closeModal();
      fetchGoals();
    } catch (err: any) {
      setSaveError(err.message || t('aiCoach.goals.saveFailed') || 'Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/training/goals/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete');
      setDeleteConfirmId(null);
      fetchGoals();
    } catch (err) {
      // error handled silently
    }
  };

  const handleFileUpload = async (file: File) => {
    const MAX_SIZE = 500 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      alert('File too large. Maximum size: 500MB');
      return;
    }
    setUploading(true);
    setUploadSuccess(false);
    setUploadError(null);
    try {
      let data;
      // All files: upload to GCS → backend extracts text → sends to AI
      const urlRes = await fetch(`${API_BASE_URL}/api/web/goals/extract/upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ filename: file.name, content_type: file.type || 'application/octet-stream' }),
      });
      if (!urlRes.ok) throw new Error('Failed to get upload URL');
      const { upload_url, blob_name } = await urlRes.json();
      const uploadRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('File upload failed');
      const extractRes = await fetch(`${API_BASE_URL}/api/web/goals/extract-from-gcs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
          body: JSON.stringify({ blob_name }),
        });
        if (!extractRes.ok) throw new Error('Extraction failed');
        data = await extractRes.json();

      setFormData({
        title: data.title || '',
        description: data.description || '',
        event_type: data.event_type || '',
        target_date: data.target_date || '',
        priority: data.priority || 'primary',
        number_of_days: data.number_of_days?.toString() || '',
        total_distance: data.total_distance?.toString() || '',
        total_elevation: data.total_elevation?.toString() || '',
      });
      setExtractedEventData(data.event_data || null);
      setFormMode('manual');
      setUploadSuccess(true);
    } catch (err) {
      setUploadError(t('aiCoach.goals.extractionFailed'));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = () => {
    setDragOver(false);
  };

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
  };

  const toggleExpanded = (id: number) => {
    setExpandedGoals(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const attachStravaRoute = async (goalId: number) => {
    if (!stravaUrl.trim()) return;
    setRouteUploading(true);
    setRouteError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/goals/${goalId}/route/strava`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: stravaUrl }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Failed'); }
      setRouteMode('none');
      setStravaUrl('');
      fetchGoals();
    } catch (err: any) {
      setRouteError(err.message || 'Failed to attach Strava route');
    } finally {
      setRouteUploading(false);
    }
  };

  const attachGpxFile = async (goalId: number, file: File) => {
    setRouteUploading(true);
    setRouteError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE_URL}/api/web/goals/${goalId}/route/gpx`, {
        method: 'POST', credentials: 'include',
        body: form,
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Failed'); }
      setRouteMode('none');
      fetchGoals();
    } catch (err: any) {
      setRouteError(err.message || 'Failed to upload GPX file');
    } finally {
      setRouteUploading(false);
    }
  };

  const attachActivityRoute = async (goalId: number) => {
    if (!activityId.trim()) return;
    setRouteUploading(true);
    setRouteError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/web/goals/${goalId}/route/activity`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ activity_id: parseInt(activityId) }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Failed'); }
      setRouteMode('none');
      setActivityId('');
      fetchGoals();
    } catch (err: any) {
      setRouteError(err.message || 'Failed to attach activity route');
    } finally {
      setRouteUploading(false);
    }
  };

  const removeRoute = async (goalId: number) => {
    try {
      await fetch(`${API_BASE_URL}/api/web/goals/${goalId}/route`, {
        method: 'DELETE', credentials: 'include',
      });
      fetchGoals();
    } catch {
      // silent
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="tg-loading">
        <div className="tg-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tg-error">
        <p>{error}</p>
        <button onClick={fetchGoals}>{t('common.refresh')}</button>
      </div>
    );
  }

  return (
    <div className="training-goals">
      <div className="tg-header">
        <h2>{t('aiCoach.goals.title')}</h2>
        <div className="tg-header-actions">
          <button className="tg-add-btn tg-add-btn-secondary" onClick={() => openFitnessModal()}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <circle cx="12" cy="12" r="6" />
              <circle cx="12" cy="12" r="2" />
            </svg>
            Add Fitness Goal
          </button>
          <button className="tg-add-btn" onClick={openAddModal}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
              <line x1="4" y1="22" x2="4" y2="15" />
            </svg>
            Add Event Goal
          </button>
        </div>
      </div>

      <div className="tg-race-calendar">
        <div className="tg-race-header">
          <h3>Race Calendar {new Date().getFullYear()}</h3>
          <button className="tg-add-event-btn" onClick={() => { setShowEventForm(true); setEditingEventIdx(null); setEventForm({ name: '', date: '', priority: 'B' }); }}>+ Add Event</button>
        </div>

        {showEventForm && (
          <div className="tg-event-form">
            <input type="text" placeholder="Event name" value={eventForm.name} onChange={e => setEventForm({...eventForm, name: e.target.value})} />
            <input type="date" value={eventForm.date} onChange={e => setEventForm({...eventForm, date: e.target.value})} />
            <div className="tg-priority-selector">
              {['A', 'B', 'C'].map(p => (
                <button key={p} className={`tg-priority-btn ${eventForm.priority === p ? 'active' : ''} priority-${p.toLowerCase()}`}
                  onClick={() => setEventForm({...eventForm, priority: p})}>
                  {p}
                </button>
              ))}
            </div>
            <div className="tg-event-form-actions">
              <button onClick={saveEvent}>{editingEventIdx !== null ? 'Update' : 'Add'}</button>
              <button onClick={() => setShowEventForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {events.length === 0 && !showEventForm && (
          <div className="tg-no-events">No events planned. Add your target races to get periodized training.</div>
        )}

        <div className="tg-event-list">
          {[...events].sort((a, b) => a.date.localeCompare(b.date)).map((ev, idx) => {
            const d = new Date(ev.date + 'T00:00:00');
            const daysUntil = Math.ceil((d.getTime() - Date.now()) / 86400000);
            return (
              <div key={idx} className={`tg-event-row priority-${ev.priority.toLowerCase()}`}>
                <span className={`tg-event-priority priority-${ev.priority.toLowerCase()}`}>{ev.priority}</span>
                <span className="tg-event-name">{ev.name}</span>
                <span className="tg-event-date">{d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                <span className="tg-event-countdown">{daysUntil > 0 ? `${daysUntil}d` : daysUntil === 0 ? 'Today' : 'Past'}</span>
                <button className="tg-event-edit" onClick={() => { setEditingEventIdx(idx); setEventForm({ name: ev.name, date: ev.date, priority: ev.priority }); setShowEventForm(true); }}>Edit</button>
                <button className="tg-event-delete" onClick={() => deleteEvent(idx)}>Delete</button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fitness Goals Section */}
      {(() => {
        const fitnessGoals = goals.filter(g => g.goal_category === 'fitness');
        const eventGoals = goals.filter(g => g.goal_category !== 'fitness');

        return (
          <>
            {fitnessGoals.length > 0 && (
              <div className="tg-section">
                <h3 className="tg-section-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="6" />
                    <circle cx="12" cy="12" r="2" />
                  </svg>
                  Fitness Goals
                </h3>
                <div className="tg-fitness-list">
                  {fitnessGoals.map(goal => {
                    const current = goal.current_value ?? 0;
                    const target = goal.target_value ?? 0;
                    const metricLabel = goal.metric_type === 'ftp' ? 'FTP' : 'Weight';
                    const unit = goal.metric_type === 'ftp' ? 'W' : 'kg';
                    const diff = target - current;
                    const progress = diff !== 0 ? Math.min(100, Math.max(0, ((current - (current - Math.abs(diff))) / Math.abs(diff)) * 100)) : 100;
                    // For weight loss (target < current), progress is how much we've lost
                    // For FTP gain (target > current), progress is how much we've gained
                    // Since we just set the goal, progress starts at 0
                    const actualProgress = 0; // Will be meaningful when current_value is tracked over time
                    const daysRemaining = goal.target_date
                      ? Math.max(0, Math.ceil((new Date(goal.target_date + 'T00:00:00').getTime() - Date.now()) / 86400000))
                      : null;

                    return (
                      <div key={goal.id} className="tg-fitness-card">
                        <div className="tg-fitness-card-header">
                          <span className="tg-fitness-metric-label">{metricLabel}</span>
                          <div className="tg-card-actions">
                            <button className="tg-icon-btn" onClick={() => openFitnessModal(goal)} title="Edit">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button className="tg-icon-btn tg-icon-btn-danger" onClick={() => setDeleteConfirmId(goal.id)} title="Delete">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        <div className="tg-fitness-values">
                          <span className="tg-fitness-current">{current}{unit}</span>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tg-fitness-arrow">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                          <span className="tg-fitness-target">{target}{unit}</span>
                        </div>
                        <div className="tg-fitness-progress-bar">
                          <div className="tg-fitness-progress-fill" style={{ width: `${actualProgress}%` }} />
                        </div>
                        <div className="tg-fitness-meta">
                          <span className="tg-fitness-diff">
                            {diff > 0 ? '+' : ''}{diff}{unit} to go
                          </span>
                          {daysRemaining !== null && (
                            <span className="tg-fitness-days">
                              {daysRemaining > 0 ? `${daysRemaining} days remaining` : daysRemaining === 0 ? 'Due today' : 'Overdue'}
                            </span>
                          )}
                        </div>
                        {onGeneratePlan && (
                          <button
                            className="tg-generate-plan-btn"
                            onClick={() => onGeneratePlan(goal.title)}
                            style={{ marginTop: 8 }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                            Wygeneruj plan treningowy
                          </button>
                        )}
                        {deleteConfirmId === goal.id && (
                          <div className="tg-delete-confirm">
                            <p>{t('aiCoach.goals.deleteConfirm')}</p>
                            <div className="tg-delete-actions">
                              <button className="tg-btn-secondary" onClick={() => setDeleteConfirmId(null)}>{t('common.cancel')}</button>
                              <button className="tg-btn-danger" onClick={() => handleDelete(goal.id)}>{t('common.delete')}</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Event Goals Section */}
            {eventGoals.length > 0 && (
              <div className="tg-section">
                <h3 className="tg-section-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                    <line x1="4" y1="22" x2="4" y2="15" />
                  </svg>
                  Event Goals
                </h3>
                <div className="tg-list">
                  {eventGoals.map(goal => {
            const stages = goal.event_data?.stages;
            const stageCount = stages?.length || 0;
            const isExpanded = expandedGoals.has(goal.id);

            return (
              <div key={goal.id} className="tg-card">
                <div className="tg-card-header">
                  <div className="tg-card-title-row">
                    <span className="tg-event-icon">
                      {EVENT_TYPE_ICONS[goal.event_type || 'other'] || EVENT_TYPE_ICONS.other}
                    </span>
                    <h3 className="tg-card-title">{goal.title}</h3>
                    <span className={`tg-priority-dot tg-priority-${goal.priority}`} />
                    {goal.event_type && (
                      <span className="tg-event-badge">
                        {t(`aiCoach.goals.eventTypes.${goal.event_type}`) || goal.event_type}
                      </span>
                    )}
                    {stageCount > 0 && (
                      <span className="tg-stage-badge" onClick={() => toggleExpanded(goal.id)}>
                        {stageCount} {t('aiCoach.goals.stages')}
                      </span>
                    )}
                  </div>
                  <div className="tg-card-actions">
                    <button className="tg-icon-btn" onClick={() => openEditModal(goal)} title={t('aiCoach.goals.editGoal')}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      className="tg-icon-btn tg-icon-btn-danger"
                      onClick={() => setDeleteConfirmId(goal.id)}
                      title={t('aiCoach.goals.deleteGoal')}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                </div>

                {goal.description && (
                  <p className="tg-card-description">{goal.description}</p>
                )}

                {onGeneratePlan && (
                  <button
                    className="tg-generate-plan-btn"
                    onClick={() => onGeneratePlan(goal.title)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                    Wygeneruj plan treningowy
                  </button>
                )}

                <div className="tg-card-meta">
                  {goal.target_date && (
                    <span className="tg-meta-item">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      {formatDate(goal.target_date)}
                    </span>
                  )}
                  {goal.total_distance != null && (
                    <span className="tg-meta-item">
                      {t('aiCoach.goals.distance', { value: Math.round(goal.total_distance) })}
                    </span>
                  )}
                  {goal.total_elevation != null && (
                    <span className="tg-meta-item">
                      {t('aiCoach.goals.elevation', { value: Math.round(goal.total_elevation) })}
                    </span>
                  )}
                  {goal.number_of_days != null && (
                    <span className="tg-meta-item">
                      {goal.number_of_days} {t('aiCoach.goals.form.numberOfDays').toLowerCase()}
                    </span>
                  )}
                </div>

                {goal.route_polyline && (
                  <div className="tg-route-preview">
                    <PolylinePreview polyline={goal.route_polyline} />
                    <div className="tg-route-meta">
                      {goal.route_distance != null && <span>{(goal.route_distance / 1000).toFixed(1)} km</span>}
                      {goal.route_elevation_gain != null && <span>{Math.round(goal.route_elevation_gain)}m elev</span>}
                      {goal.route_source && <span className="tg-route-source">{goal.route_source}</span>}
                    </div>
                  </div>
                )}

                {goal.event_data?.countries && goal.event_data.countries.length > 0 && (
                  <div className="tg-countries">
                    <span className="tg-meta-label">{t('aiCoach.goals.countries')}:</span>
                    {goal.event_data.countries.join(', ')}
                  </div>
                )}

                {goal.event_data?.highlights && goal.event_data.highlights.length > 0 && (
                  <div className="tg-highlights">
                    <span className="tg-meta-label">{t('aiCoach.goals.highlights')}:</span>
                    {goal.event_data.highlights.join(', ')}
                  </div>
                )}

                {stageCount > 0 && isExpanded && (
                  <div className="tg-stages">
                    <h4 className="tg-stages-title">{t('aiCoach.goals.stages')}</h4>
                    {stages!.map((stage, idx) => (
                      <div key={idx} className="tg-stage">
                        <div className="tg-stage-header">
                          <span className="tg-stage-number">
                            {t('aiCoach.goals.stage', { number: stage.number })}
                          </span>
                          {stage.title && <span className="tg-stage-title">{stage.title}</span>}
                          {stage.date && <span className="tg-stage-date">{formatDate(stage.date)}</span>}
                        </div>
                        <div className="tg-stage-meta">
                          {stage.distance != null && (
                            <span>{t('aiCoach.goals.distance', { value: Math.round(stage.distance) })}</span>
                          )}
                          {stage.elevation != null && (
                            <span>{t('aiCoach.goals.elevation', { value: Math.round(stage.elevation) })}</span>
                          )}
                          {stage.start_location && stage.end_location && (
                            <span>{stage.start_location} &rarr; {stage.end_location}</span>
                          )}
                        </div>
                        {stage.climbs && stage.climbs.length > 0 && (
                          <div className="tg-climbs">
                            <span className="tg-climbs-label">{t('aiCoach.goals.climbs')}:</span>
                            <div className="tg-climbs-list">
                              {stage.climbs.map((climb, ci) => (
                                <span key={ci} className="tg-climb-tag">
                                  {climb.name}
                                  {climb.elevation_gain != null && ` (${climb.elevation_gain}m)`}
                                  {climb.avg_gradient != null && ` ${climb.avg_gradient}%`}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {!stageCount && goal.daily_stages && goal.daily_stages.length > 0 && isExpanded && (
                  <div className="tg-stages">
                    <h4 className="tg-stages-title">Per-Day Breakdown</h4>
                    {goal.daily_stages.map((day: any, idx: number) => {
                      const dayDate = goal.target_date ? (() => {
                        const d = new Date(goal.target_date + 'T12:00:00');
                        d.setDate(d.getDate() + (day.day_number - 1));
                        return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                      })() : null;
                      return (
                        <div key={idx} className="tg-stage">
                          <div className="tg-stage-header">
                            <span className="tg-stage-number">Day {day.day_number}</span>
                            {day.title && <span className="tg-stage-title">{day.title}</span>}
                            {dayDate && <span className="tg-stage-date">{dayDate}</span>}
                          </div>
                          <div className="tg-stage-meta">
                            {day.distance != null && <span>{Math.round(day.distance)} km</span>}
                            {day.elevation != null && <span>{Math.round(day.elevation)}m elev</span>}
                          </div>
                          {day.description && (
                            <p className="tg-day-description">{day.description}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {(stageCount > 0 || (goal.daily_stages && goal.daily_stages.length > 0)) && !isExpanded && (
                  <button className="tg-expand-btn" onClick={() => toggleExpanded(goal.id)}>
                    Show {stageCount || goal.daily_stages?.length || 0} days
                  </button>
                )}

                {deleteConfirmId === goal.id && (
                  <div className="tg-delete-confirm">
                    <p>{t('aiCoach.goals.deleteConfirm')}</p>
                    <div className="tg-delete-actions">
                      <button className="tg-btn-secondary" onClick={() => setDeleteConfirmId(null)}>
                        {t('common.cancel')}
                      </button>
                      <button className="tg-btn-danger" onClick={() => handleDelete(goal.id)}>
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
                </div>
              </div>
            )}

            {goals.length === 0 && (
              <div className="tg-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4l3 3" />
                </svg>
                <p className="tg-empty-title">{t('aiCoach.goals.empty')}</p>
                <p className="tg-empty-subtitle">{t('aiCoach.goals.emptySubtitle')}</p>
              </div>
            )}
          </>
        );
      })()}

      {/* Fitness Goal Modal */}
      {showFitnessModal && (
        <div className="tg-modal-overlay" onClick={closeFitnessModal}>
          <div className="tg-modal" onClick={e => e.stopPropagation()}>
            <div className="tg-modal-header">
              <h3>{editingFitnessGoal ? 'Edit Fitness Goal' : 'Add Fitness Goal'}</h3>
              <button className="tg-modal-close" onClick={closeFitnessModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="tg-form">
              <div className="tg-form-group">
                <label>Metric</label>
                <select
                  value={fitnessFormData.metric_type}
                  onChange={e => setFitnessFormData(prev => ({ ...prev, metric_type: e.target.value }))}
                >
                  <option value="weight">Weight (kg)</option>
                  <option value="ftp">FTP (W)</option>
                </select>
              </div>

              <div className="tg-form-row">
                <div className="tg-form-group">
                  <label>Current Value</label>
                  <input
                    type="number"
                    value={fitnessFormData.current_value}
                    onChange={e => setFitnessFormData(prev => ({ ...prev, current_value: e.target.value }))}
                    placeholder={fitnessFormData.metric_type === 'weight' ? 'e.g. 78' : 'e.g. 250'}
                    step="0.1"
                    min="0"
                  />
                </div>
                <div className="tg-form-group">
                  <label>Target Value</label>
                  <input
                    type="number"
                    value={fitnessFormData.target_value}
                    onChange={e => setFitnessFormData(prev => ({ ...prev, target_value: e.target.value }))}
                    placeholder={fitnessFormData.metric_type === 'weight' ? 'e.g. 72' : 'e.g. 280'}
                    step="0.1"
                    min="0"
                  />
                </div>
              </div>

              <div className="tg-form-group">
                <label>Target Date</label>
                <input
                  type="date"
                  value={fitnessFormData.target_date}
                  onChange={e => setFitnessFormData(prev => ({ ...prev, target_date: e.target.value }))}
                />
              </div>

              {fitnessSaveError && (
                <div className="tg-save-error">{fitnessSaveError}</div>
              )}

              <div className="tg-form-actions">
                <button className="tg-btn-secondary" onClick={closeFitnessModal}>
                  {t('common.cancel')}
                </button>
                <button
                  className="tg-btn-primary"
                  onClick={handleFitnessSave}
                  disabled={fitnessSaving || !fitnessFormData.current_value || !fitnessFormData.target_value}
                >
                  {fitnessSaving ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="tg-modal-overlay" onClick={closeModal}>
          <div className="tg-modal" onClick={e => e.stopPropagation()}>
            <div className="tg-modal-header">
              <h3>{editingGoal ? 'Edit Event Goal' : 'Add Event Goal'}</h3>
              <button className="tg-modal-close" onClick={closeModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {!editingGoal && (
              <div className="tg-mode-toggle">
                <button
                  className={`tg-mode-btn ${formMode === 'manual' ? 'tg-mode-btn-active' : ''}`}
                  onClick={() => setFormMode('manual')}
                >
                  {t('aiCoach.goals.manual')}
                </button>
                <button
                  className={`tg-mode-btn ${formMode === 'upload' ? 'tg-mode-btn-active' : ''}`}
                  onClick={() => setFormMode('upload')}
                >
                  {t('aiCoach.goals.uploadFile')}
                </button>
              </div>
            )}

            {uploadSuccess && (
              <div className="tg-upload-success">{t('aiCoach.goals.extracted')}</div>
            )}
            {uploadError && (
              <div className="tg-upload-error">{uploadError}</div>
            )}

            {formMode === 'upload' && !editingGoal ? (
              <div className="tg-upload-section">
                {uploading ? (
                  <div className="tg-upload-loading">
                    <div className="tg-spinner" />
                    <p>{t('aiCoach.goals.extracting')}</p>
                  </div>
                ) : (
                  <div
                    className={`tg-dropzone ${dragOver ? 'tg-dropzone-active' : ''}`}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <p className="tg-dropzone-title">{t('aiCoach.goals.dragDrop')}</p>
                    <p className="tg-dropzone-subtitle">{t('aiCoach.goals.dragDropSubtitle')}</p>
                    <p className="tg-dropzone-browse">{t('aiCoach.goals.orBrowse')}</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="tg-file-input"
                      accept="*/*"
                      onChange={onFileSelect}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="tg-form">
                <div className="tg-form-group">
                  <label>{t('aiCoach.goals.form.title')}</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => handleFormChange('title', e.target.value)}
                    placeholder={t('aiCoach.goals.form.title')}
                  />
                </div>

                <div className="tg-form-group">
                  <label>{t('aiCoach.goals.form.description')}</label>
                  <textarea
                    value={formData.description}
                    onChange={e => handleFormChange('description', e.target.value)}
                    placeholder={t('aiCoach.goals.form.description')}
                    rows={3}
                  />
                </div>

                <div className="tg-form-row">
                  <div className="tg-form-group">
                    <label>{t('aiCoach.goals.form.eventType')}</label>
                    <select
                      value={formData.event_type}
                      onChange={e => handleFormChange('event_type', e.target.value)}
                    >
                      <option value="">--</option>
                      {EVENT_TYPES.map(et => (
                        <option key={et} value={et}>
                          {t(`aiCoach.goals.eventTypes.${et}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="tg-form-group">
                    <label>{t('aiCoach.goals.form.targetDate')}</label>
                    <input
                      type="date"
                      value={formData.target_date}
                      onChange={e => handleFormChange('target_date', e.target.value)}
                    />
                  </div>
                </div>

                <div className="tg-form-row">
                  <div className="tg-form-group">
                    <label>{t('aiCoach.goals.form.priority')}</label>
                    <select
                      value={formData.priority}
                      onChange={e => handleFormChange('priority', e.target.value)}
                    >
                      <option value="primary">{t('aiCoach.goals.form.primary')}</option>
                      <option value="secondary">{t('aiCoach.goals.form.secondary')}</option>
                    </select>
                  </div>

                  <div className="tg-form-group">
                    <label>{t('aiCoach.goals.form.numberOfDays')}</label>
                    <input
                      type="number"
                      value={formData.number_of_days}
                      onChange={e => handleFormChange('number_of_days', e.target.value)}
                      min="1"
                    />
                  </div>
                </div>

                <div className="tg-form-row">
                  <div className="tg-form-group">
                    <label>{t('aiCoach.goals.form.totalDistance')}</label>
                    <input
                      type="number"
                      value={formData.total_distance}
                      onChange={e => handleFormChange('total_distance', e.target.value)}
                      min="0"
                      step="0.1"
                    />
                  </div>

                  <div className="tg-form-group">
                    <label>{t('aiCoach.goals.form.totalElevation')}</label>
                    <input
                      type="number"
                      value={formData.total_elevation}
                      onChange={e => handleFormChange('total_elevation', e.target.value)}
                      min="0"
                    />
                  </div>
                </div>

                {editingGoal && (
                  <div className="tg-route-section">
                    <label className="tg-route-section-label">Route / Track</label>
                    {editingGoal.route_polyline ? (
                      <div className="tg-route-attached">
                        <PolylinePreview polyline={editingGoal.route_polyline} width={400} height={140} />
                        <div className="tg-route-attached-meta">
                          {editingGoal.route_distance != null && <span>{(editingGoal.route_distance / 1000).toFixed(1)} km</span>}
                          {editingGoal.route_elevation_gain != null && <span>{Math.round(editingGoal.route_elevation_gain)}m elev</span>}
                          {editingGoal.route_source && <span className="tg-route-source">{editingGoal.route_source}</span>}
                          <button className="tg-route-remove" onClick={() => removeRoute(editingGoal.id)}>Remove route</button>
                        </div>
                      </div>
                    ) : (
                      <div className="tg-route-attach">
                        <div className="tg-route-mode-btns">
                          <button className={routeMode === 'strava' ? 'active' : ''} onClick={() => { setRouteMode('strava'); setRouteError(null); }}>Strava Route</button>
                          <button className={routeMode === 'gpx' ? 'active' : ''} onClick={() => { setRouteMode('gpx'); setRouteError(null); }}>Upload GPX</button>
                          <button className={routeMode === 'activity' ? 'active' : ''} onClick={() => { setRouteMode('activity'); setRouteError(null); }}>From Activity</button>
                        </div>
                        {routeError && <div className="tg-route-error">{routeError}</div>}
                        {routeMode === 'strava' && (
                          <div className="tg-route-input-row">
                            <input type="text" placeholder="https://www.strava.com/routes/..." value={stravaUrl} onChange={e => setStravaUrl(e.target.value)} />
                            <button onClick={() => attachStravaRoute(editingGoal.id)} disabled={routeUploading || !stravaUrl.trim()}>
                              {routeUploading ? 'Attaching...' : 'Attach'}
                            </button>
                          </div>
                        )}
                        {routeMode === 'gpx' && (
                          <div className="tg-route-input-row">
                            <button onClick={() => gpxInputRef.current?.click()} disabled={routeUploading}>
                              {routeUploading ? 'Uploading...' : 'Choose GPX file'}
                            </button>
                            <input ref={gpxInputRef} type="file" accept=".gpx" style={{ display: 'none' }}
                              onChange={e => { const f = e.target.files?.[0]; if (f) attachGpxFile(editingGoal.id, f); }} />
                          </div>
                        )}
                        {routeMode === 'activity' && (
                          <div className="tg-route-input-row">
                            <input type="number" placeholder="Activity ID" value={activityId} onChange={e => setActivityId(e.target.value)} />
                            <button onClick={() => attachActivityRoute(editingGoal.id)} disabled={routeUploading || !activityId.trim()}>
                              {routeUploading ? 'Attaching...' : 'Attach'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {saveError && (
                  <div className="tg-save-error">{saveError}</div>
                )}

                <div className="tg-form-actions">
                  <button className="tg-btn-secondary" onClick={closeModal}>
                    {t('common.cancel')}
                  </button>
                  <button
                    className="tg-btn-primary"
                    onClick={handleSave}
                    disabled={saving || !formData.title.trim()}
                  >
                    {saving ? t('common.loading') : t('common.save')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <DecisionHistory />
    </div>
  );
};

export default TrainingGoals;
